/**
 * Sprint 10 — /usage cost-observability dashboard.
 *
 * Surfaces `llm_token_usage` rows the platform already writes:
 *  - Headline: "Maya's research spent $X on signals this {window}"
 *  - 4 KPI tiles (total cost, calls, tokens, signals share)
 *  - Daily timeseries (tokens + cost)
 *  - Per-agent breakdown table with provider/model
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Radar, Sparkles, DollarSign, Zap, RefreshCw, Activity } from "lucide-react";
import { usageApi } from "@/lib/api";

const WINDOWS = [
  { id: "day",   label: "24h" },
  { id: "week",  label: "7d" },
  { id: "month", label: "30d" },
  { id: "all",   label: "All time" },
];

function $(n) {
  if (n == null) return "$0";
  if (n < 0.01)  return "<$0.01";
  if (n < 1)     return `$${n.toFixed(3)}`;
  if (n < 1000)  return `$${n.toFixed(2)}`;
  return `$${(n / 1000).toFixed(2)}k`;
}

function nfmt(n) {
  if (n == null) return "0";
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export default function UsagePage() {
  const [window, setWindow] = useState("week");
  const [state, setState] = useState({ loading: true, data: null, err: null });

  async function load() {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const data = await usageApi.get(window);
      setState({ loading: false, data, err: null });
    } catch (e) {
      setState({ loading: false, data: null, err: e.message || "Failed to load" });
    }
  }

  useEffect(() => { queueMicrotask(load); }, [window]);

  const d = state.data;
  const total = d?.total || { calls: 0, tokens_total: 0, cost_usd: 0 };
  const headline = d?.headline_signals || { calls: 0, tokens: 0, cost_usd: 0 };
  const signalsShare = total.cost_usd > 0 ? Math.round((headline.cost_usd / total.cost_usd) * 100) : 0;

  return (
    <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin bg-white" data-testid="usage-page">
      <div className="max-w-[1100px] mx-auto w-full px-4 sm:px-8 py-6 sm:py-10 space-y-6 sm:space-y-8 min-w-0">

        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
          <div className="min-w-0">
            <h1 className="text-[24px] sm:text-[28px] font-semibold tracking-tight text-slate-900">Usage &amp; cost</h1>
            <p className="mt-1.5 text-[13px] sm:text-[13.5px] text-slate-500 max-w-[640px] leading-relaxed">
              Where Maya is spending your AI budget. Token counts and cost estimates pull straight from <code className="text-[11.5px] bg-slate-100 px-1 rounded break-all">llm_token_usage</code> — no rounding, no marketing math.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 min-w-0 w-full sm:w-auto">
            <div className="rounded-full ring-1 ring-slate-200 bg-slate-50 p-0.5 flex items-center flex-wrap sm:flex-nowrap flex-1 sm:flex-initial min-w-0" data-testid="usage-window-tabs">
              {WINDOWS.map((w) => (
                <button key={w.id} type="button" onClick={() => setWindow(w.id)}
                  className={cn("min-h-9 sm:h-7 px-2.5 sm:px-3 text-[11.5px] rounded-full font-medium transition-colors flex-1 sm:flex-none",
                    window === w.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
                  data-testid={`usage-window-${w.id}`}>
                  {w.label}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" className="h-10 w-10 sm:h-8 sm:w-8 shrink-0 text-slate-500" onClick={load} disabled={state.loading}
              data-testid="usage-refresh" aria-label="Refresh usage">
              <RefreshCw size={12} className={cn(state.loading && "animate-spin")} />
            </Button>
          </div>
        </header>

        {state.err && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-700 break-words">
            Couldn&apos;t load usage: {state.err}
          </div>
        )}

        {/* Headline: signals cost-vs-value */}
        <section className="rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/60 via-white to-rose-50/40 px-4 sm:px-7 py-5 sm:py-6 min-w-0"
          data-testid="usage-headline-signals">
          <div className="text-[11px] uppercase tracking-wider text-fuchsia-700 font-semibold inline-flex items-center gap-1.5">
            <Radar size={12} /> Maya&apos;s research · {d?.window_label?.toLowerCase()}
          </div>
          {state.loading ? <Skeleton className="mt-3 h-7 w-2/3" /> : (
            <p className="mt-2.5 text-[16px] sm:text-[18px] leading-relaxed text-slate-800 break-words">
              Maya spent <span className="font-bold tabular-nums text-fuchsia-700">{$( headline.cost_usd )}</span> on community signals
              {" "}across <span className="font-semibold tabular-nums">{headline.calls}</span> LLM calls
              {" "}({nfmt(headline.tokens)} tokens).
              {total.cost_usd > 0 && (
                <span className="ml-1 text-slate-500">That&apos;s {signalsShare}% of your total spend.</span>
              )}
            </p>
          )}
        </section>

        {/* KPI tiles */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="usage-kpis">
          {state.loading ? ["cost", "calls", "tokens", "signals"].map((k) => <Skeleton key={`skeleton-kpi-${k}`} className="h-[92px] rounded-lg" />)
            : [
                { k: "cost",    label: "Total cost",   v: $( total.cost_usd ),       sub: d?.window_label, Icon: DollarSign },
                { k: "calls",   label: "LLM calls",    v: nfmt(total.calls),         sub: "all agents",     Icon: Zap },
                { k: "tokens",  label: "Tokens",       v: nfmt(total.tokens_total),  sub: "prompt + completion", Icon: Activity },
                { k: "signals", label: "Signals share",v: total.cost_usd > 0 ? `${signalsShare}%` : "—", sub: "of cost",  Icon: Sparkles },
              ].map((tile) => (
                <div key={tile.k} className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 min-w-0" data-testid={`usage-kpi-${tile.k}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10.5px] font-medium uppercase tracking-wider text-slate-500 truncate">{tile.label}</div>
                    <tile.Icon size={12} className="text-slate-400 shrink-0" />
                  </div>
                  <div className="mt-1 text-[20px] sm:text-[24px] font-semibold tracking-tight text-slate-900 tabular-nums">{tile.v}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">{tile.sub}</div>
                </div>
              ))}
        </section>

        {/* Timeseries */}
        <section className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 min-w-0" data-testid="usage-timeseries">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between mb-2">
            <h2 className="text-[14px] font-semibold tracking-tight text-slate-900">Daily spend</h2>
            <span className="text-[10.5px] text-slate-500">tokens (left) · cost (right)</span>
          </div>
          <div className="h-48 sm:h-56 w-full min-w-0">
            {(() => {
              if (state.loading) return <Skeleton className="h-full w-full rounded" />;
              if ((d?.timeseries || []).length === 0) {
                return (
                  <div className="h-full flex items-center justify-center text-[12px] text-slate-400">
                    No LLM activity in this window.
                  </div>
                );
              }
              return (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={d.timeseries} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="tokensFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="#a78bfa" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} minTickGap={24} />
                    <YAxis yAxisId="L" width={36} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="R" width={36} orientation="right" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 8 }} />
                    <Area yAxisId="L" dataKey="tokens" type="monotone" stroke="#7c3aed" fill="url(#tokensFill)" strokeWidth={2} />
                    <Area yAxisId="R" dataKey="cost_usd" type="monotone" stroke="#db2777" fill="transparent" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
        </section>

        {/* Per-agent breakdown */}
        <section className="rounded-lg border border-slate-200 bg-white overflow-hidden min-w-0" data-testid="usage-by-agent">
          <div className="px-3 sm:px-4 py-3 border-b border-slate-100">
            <h2 className="text-[14px] font-semibold tracking-tight text-slate-900">Per agent</h2>
          </div>
            {(() => {
              if (state.loading) return <Skeleton className="h-[140px]" />;
              if ((d?.by_agent || []).length === 0) {
                return <div className="px-4 py-8 text-center text-[12px] text-slate-400">No agent activity yet.</div>;
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px] text-[12.5px]">
                    <thead className="bg-slate-50/60"><tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-2 font-medium">Agent</th>
                      <th className="px-4 py-2 font-medium text-right">Calls</th>
                      <th className="px-4 py-2 font-medium text-right">Tokens</th>
                      <th className="px-4 py-2 font-medium text-right">Cost</th>
                    </tr></thead>
                    <tbody>
                      {d.by_agent.map((a) => (
                        <tr key={a.agent_id} className="border-t border-slate-100" data-testid={`usage-agent-row-${a.agent_id}`}>
                          <td className="px-4 py-2.5 font-medium text-slate-900">{a.agent_label}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{nfmt(a.calls)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{nfmt(a.tokens)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{$( a.cost_usd )}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
        </section>

      </div>
    </div>
  );
}
